import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PlotlyLineDash, PlotlyMarkerSymbol } from '../../../../../../types/graphic-metadata.interface';

interface LineStyle {
  color?: string;
  width?: number;
  dash?: PlotlyLineDash;
}

interface MarkerStyle {
  symbol?: PlotlyMarkerSymbol | (string & {}) | undefined;
  color?: string | undefined;
}

const DEFAULT_COLOR = '#1f77b4';
const CROSS_STROKE_WIDTH = 2;
const MARKER_STROKE_WIDTH = 1;

const SVG_WIDTH = 40;
const SVG_HEIGHT = 16;
const SVG_CENTER_Y = SVG_HEIGHT / 2;
const SVG_CENTER_X = SVG_WIDTH / 2;
const SVG_VIEWBOX = `0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`;

const MARKER_GAP = 7;
const CENTER_DASH_HALF = 3;
const LEFT_SEGMENT_END = SVG_CENTER_X - MARKER_GAP;
const RIGHT_SEGMENT_START = SVG_CENTER_X + MARKER_GAP;

const DEFAULT_LINE_WIDTH = 2;
const LINE_WIDTH_THIN_MAX = 2;
const LINE_WIDTH_MEDIUM_MAX = 5;
const STROKE_WIDTH_THIN = 1.5;
const STROKE_WIDTH_MEDIUM = 2.5;
const STROKE_WIDTH_THICK = 3.5;

const MARKER_PATHS: Record<string, string> = {
  'circle': 'M0,-5 A5,5 0 1,1 0,5 A5,5 0 1,1 0,-5',
  'square': 'M-4,-4 h8 v8 h-8 z',
  'diamond': 'M0,-6 L5,0 L0,6 L-5,0 Z',
  'cross': 'M-5,0 h10 M0,-5 v10',
  'x': 'M-4,-4 L4,4 M4,-4 L-4,4',
  'triangle-up': 'M0,-5 L5,4 L-5,4 Z',
  'triangle-down': 'M0,5 L5,-4 L-5,-4 Z',
  'pentagon': 'M0,-5 L4.76,-1.55 L2.94,4.05 L-2.94,4.05 L-4.76,-1.55 Z',
  'hexagon': 'M0,-5 L4.33,-2.5 L4.33,2.5 L0,5 L-4.33,2.5 L-4.33,-2.5 Z',
  'star': 'M0,-6 L1.4,-1.8 L6,-1.8 L2.3,1 L3.5,5.5 L0,2.5 L-3.5,5.5 L-2.3,1 L-6,-1.8 L-1.4,-1.8 Z',
  'asterisk': 'M0,-5 v10 M-4.33,-2.5 L4.33,2.5 M4.33,-2.5 L-4.33,2.5',
  'hourglass': 'M-4,-5 L4,-5 L0,0 L4,5 L-4,5 L0,0 Z',
  'bowtie': 'M-5,-4 L0,0 L-5,4 Z M5,-4 L0,0 L5,4 Z',
  'circle-cross': 'M0,-5 A5,5 0 1,1 0,5 A5,5 0 1,1 0,-5 M-3.5,0 h7 M0,-3.5 v7',
};

const DASH_PATTERNS: Record<PlotlyLineDash, string> = {
  none: '',
  solid: 'none',
  dot: '2,4',
  dash: '8,4',
  longdash: '12,4',
  dashdot: '8,4,2,4',
  longdashdot: '12,4,2,4',
};

const DASH_TYPE_KEYS: Record<PlotlyLineDash, string> = {
  none: 'graphs.curveStyles.none',
  solid: 'graphs.legend.dashTypes.solid',
  dot: 'graphs.legend.dashTypes.dot',
  dash: 'graphs.legend.dashTypes.dash',
  longdash: 'graphs.legend.dashTypes.longdash',
  dashdot: 'graphs.legend.dashTypes.dashdot',
  longdashdot: 'graphs.legend.dashTypes.longdashdot',
};

const MARKER_TYPE_KEYS: Record<string, string> = {
  'circle': 'graphs.legend.markerTypes.circle',
  'square': 'graphs.legend.markerTypes.square',
  'diamond': 'graphs.legend.markerTypes.diamond',
  'cross': 'graphs.legend.markerTypes.cross',
  'x': 'graphs.legend.markerTypes.x',
  'triangle-up': 'graphs.legend.markerTypes.triangle-up',
  'triangle-down': 'graphs.legend.markerTypes.triangle-down',
  'pentagon': 'graphs.legend.markerTypes.pentagon',
  'hexagon': 'graphs.legend.markerTypes.hexagon',
  'star': 'graphs.legend.markerTypes.star',
  'asterisk': 'graphs.legend.markerTypes.asterisk',
  'hourglass': 'graphs.legend.markerTypes.hourglass',
  'bowtie': 'graphs.legend.markerTypes.bowtie',
  'circle-cross': 'graphs.legend.markerTypes.circle-cross',
};

@Component({
  selector: 'app-legend-line',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex-shrink-0' },
  templateUrl: './legend-line.component.html',
})
export class LegendLineComponent {
  private readonly translate = inject(TranslocoService);
  readonly line = input<LineStyle>();
  readonly marker = input<MarkerStyle>();

  protected readonly svgWidth = SVG_WIDTH;
  protected readonly svgHeight = SVG_HEIGHT;
  protected readonly viewBox = SVG_VIEWBOX;
  protected readonly centerY = SVG_CENTER_Y;
  protected readonly markerX = SVG_CENTER_X;
  protected readonly markerTransform = `translate(${SVG_CENTER_X},${SVG_CENTER_Y})`;
  protected readonly centerDashHalf = CENTER_DASH_HALF;
  protected readonly leftSegmentEnd = LEFT_SEGMENT_END;
  protected readonly rightSegmentStart = RIGHT_SEGMENT_START;
  protected readonly crossStrokeWidth = CROSS_STROKE_WIDTH;
  protected readonly markerStrokeWidth = MARKER_STROKE_WIDTH;

  readonly lineColor = computed(() => this.line()?.color ?? this.marker()?.color ?? DEFAULT_COLOR);
  readonly markerColor = computed(() => this.marker()?.color ?? this.lineColor());

  private readonly dashType = computed((): PlotlyLineDash => this.line()?.dash ?? 'solid');
  readonly isLineHidden = computed(() => this.dashType() === 'none');
  readonly hasDash = computed(() => this.dashType() !== 'solid' && this.dashType() !== 'none');
  readonly dashArray = computed(() => DASH_PATTERNS[this.dashType()]);

  readonly strokeWidth = computed(() => {
    const width = this.line()?.width ?? DEFAULT_LINE_WIDTH;
    if (width <= LINE_WIDTH_THIN_MAX) return STROKE_WIDTH_THIN;
    if (width <= LINE_WIDTH_MEDIUM_MAX) return STROKE_WIDTH_MEDIUM;
    return STROKE_WIDTH_THICK;
  });

  readonly markerSymbol = computed(() => {
    const symbol = this.marker()?.symbol;
    return symbol && symbol !== 'none' ? symbol : null;
  });

  readonly markerPath = computed(() => {
    const symbol = this.markerSymbol();
    return symbol ? (MARKER_PATHS[symbol] ?? null) : null;
  });

  readonly isStrokeOnlyMarker = computed(() => {
    const symbol = this.markerSymbol();
    return symbol === 'cross' || symbol === 'x' || symbol === 'asterisk';
  });

  readonly ariaLabel = computed(() => {
    const lineLabel = this.translate.translate('graphs.legend.line');
    const dashLabel = this.translate.translate(DASH_TYPE_KEYS[this.dashType()]);
    const parts = [`${lineLabel} ${dashLabel}`];
    const symbol = this.markerSymbol();
    if (symbol) {
      const markerLabel = this.translate.translate('graphs.legend.marker');
      const symbolLabel = MARKER_TYPE_KEYS[symbol] ? this.translate.translate(MARKER_TYPE_KEYS[symbol]) : symbol;
      parts.push(`${markerLabel} ${symbolLabel}`);
    }
    return parts.join(', ');
  });
}
